/**
 * logAppError — fire-and-forget client-side error logger.
 *
 * Writes to public.app_error_logs (anyone can insert via RLS).
 * Used by the post-launch operations dashboard at /admin/post-launch.
 * Safe to call from anywhere — never throws.
 */
import { supabase } from "@/integrations/supabase/client";

export type AppErrorSeverity = "info" | "warn" | "error" | "critical";

export interface LogAppErrorOptions {
  source: string; // e.g. "client:Map", "client:SubmitPlace", "fn:places-moderate"
  message: string;
  severity?: AppErrorSeverity;
  route?: string;
  metadata?: Record<string, unknown>;
}

export async function logAppError(opts: LogAppErrorOptions): Promise<void> {
  try {
    // Drop provider-not-mounted noise — these are HMR/boot races that the
    // app already recovers from via safe fallbacks. Logging them flooded
    // app_error_logs with no actionable signal.
    if (/must be used within .*Provider/i.test(opts.message ?? "")) {
      return;
    }
    const route =
      opts.route ??
      (typeof window !== "undefined" ? window.location.pathname : undefined);
    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : undefined;

    let userId: string | null = null;
    try {
      const { data } = await supabase.auth.getUser();
      userId = data?.user?.id ?? null;
    } catch {
      userId = null;
    }

    await supabase.from("app_error_logs" as any).insert({
      source: opts.source.slice(0, 200),
      severity: opts.severity ?? "error",
      message: (opts.message ?? "Unknown error").slice(0, 2000),
      route: route?.slice(0, 500),
      user_id: userId,
      user_agent: userAgent?.slice(0, 500),
      metadata: opts.metadata ?? {},
    });
  } catch {
    // Never throw from logger — silent fail.
  }
}